const BASE = '/myaccount';

/** Absolute paths for navigation (navigate(), href, pathname checks) */
export const MY_ACCOUNT_ROUTES = {
  login: '/login',
  overview: BASE,
  /** @deprecated Use profile. Kept for redirect from /myaccount/general */
  general: `${BASE}/general`,
  profile: `${BASE}/profile`,
  vehicles: `${BASE}/profile/vehicles`,
  addresses: `${BASE}/profile/addresses`,
  cards: `${BASE}/profile/cards`,
  /** Base prefix for the per-field edit screens; not a page itself. */
  editProfile: `${BASE}/profile/edit`,
  editProfileName: `${BASE}/profile/edit/name`,
  editProfileEmail: `${BASE}/profile/edit/email`,
  editProfilePhone: `${BASE}/profile/edit/phone`,
  addAddress: `${BASE}/profile/addresses/add`,
  editAddress: (id: string) => `${BASE}/profile/addresses/edit/${id}`,
  addVehicle: `${BASE}/profile/vehicles/add`,
  editVehicle: (id: string) => `${BASE}/profile/vehicles/edit/${id}`,
  vehicleDetails: (id: string) => `${BASE}/profile/vehicles/${id}`,
  linkPermit: (id: string) => `${BASE}/profile/vehicles/${id}/link-permit`,
  paymentMethods: `${BASE}/profile/cards`,
  addCard: `${BASE}/profile/cards/add-card`,
  addBankAccount: `${BASE}/profile/cards/add-bank-account`,
  editCard: (id: string) => `${BASE}/profile/cards/edit-card/${id}`,
  editBankAccount: (id: string) => `${BASE}/profile/cards/edit-bank-account/${id}`,
  reservations: `${BASE}/reservations`,
  upcomingReservations: `${BASE}/reservations/upcoming-reservations`,
  pastReservations: `${BASE}/reservations/past-reservations`,
  guestPass: `${BASE}/guest-pass`,
  upcomingGuestPass: `${BASE}/guest-pass/upcoming-guest-pass`,
  pastGuestPass: `${BASE}/guest-pass/past-guest-pass`,
  loyalty: `${BASE}/loyalty`,
  parkingCredits: `${BASE}/parking-credits`,
  parkingHistory: `${BASE}/parking-history`,
  citations: `${BASE}/citations`,
  openCitations: `${BASE}/citations/open-citations`,
  closedCitations: `${BASE}/citations/closed-citations`,
  citationDetail: (id: string) => `${BASE}/citations/${id}`,
  settings: `${BASE}/settings`,
  parkingValidation: `${BASE}/parking-validation`,
  activeParking: `${BASE}/parking-validation/active-parking`,
  /** /myaccount/parking-validation/validate/:ticketId */
  validate: (ticketId: string) => `${BASE}/parking-validation/validate/${ticketId}`,
  eventParking: `${BASE}/parking-validation/event-parking`,
  eventParkingEvent: (eventId: string) => `${BASE}/parking-validation/event-parking/${eventId}`,
  eventParkingReservation: (eventId: string, reservationId: string) =>
    `${BASE}/parking-validation/event-parking/${eventId}/${reservationId}`,
  validationReports: `${BASE}/parking-validation/reports`,
  programs: `${BASE}/parking-validation/programs`,
  createReservation: (eventId: string) => `${BASE}/parking-validation/event-parking/${eventId}/add`,
  editReservation: (eventId: string, reservationId: string) =>
    `${BASE}/parking-validation/event-parking/${eventId}/${reservationId}/edit`,
  importReservations: (eventId: string) =>
    `${BASE}/parking-validation/event-parking/${eventId}/import`,
  vehicleCitations: (vehicleId: string) =>
    `${BASE}/citations?vehicleId=${encodeURIComponent(vehicleId)}`,
  managePermit: `${BASE}/manage-permit`,
  managePermitAccounts: `${BASE}/manage-permit/accounts`,
  managePermitTasks: `${BASE}/manage-permit/tasks`,
  managePermitAutopay: `${BASE}/manage-permit/autopay`,
  managePermitAutopaySetup: (accountId: string) =>
    `${BASE}/manage-permit/autopay/setup/${accountId}`,
  managePermitAutopayEdit: (accountId: string) => `${BASE}/manage-permit/autopay/edit/${accountId}`,
  managePermitAutopaySignContract: (accountId: string) =>
    `${BASE}/manage-permit/autopay/sign-contract/${accountId}`,
  managePermitAccountDetail: (accountId: string) => `${BASE}/manage-permit/accounts/${accountId}`,
  managePermitAccountPayment: (accountId: string) =>
    `${BASE}/manage-permit/accounts/${accountId}/payment`,
  managePermitAccountPermits: (accountId: string) =>
    `${BASE}/manage-permit/accounts/${accountId}/permits`,
  managePermitAccountTransactions: (accountId: string) =>
    `${BASE}/manage-permit/accounts/${accountId}/transactions`,
  managePermitAccountSubaccounts: (accountId: string) =>
    `${BASE}/manage-permit/accounts/${accountId}/subaccounts`,
  managePermitAccountInfo: (accountId: string) =>
    `${BASE}/manage-permit/accounts/${accountId}/account-info`,
  managePermitAccountDocuments: (accountId: string) =>
    `${BASE}/manage-permit/accounts/${accountId}/documents`,
  managePermitAccountAddTaxCert: (accountId: string) =>
    `${BASE}/manage-permit/accounts/${accountId}/documents/add-tax-certificate`,
  managePermitAccountEditContact: (accountId: string) =>
    `${BASE}/manage-permit/accounts/${accountId}/account-info/edit-contact`,
  managePermitAccountEditAddress: (accountId: string) =>
    `${BASE}/manage-permit/accounts/${accountId}/account-info/edit-address`,
  managePermitLinkedPermit: (accountId: string, permitId: string) =>
    `${BASE}/manage-permit/accounts/${accountId}/permits/${permitId}`,
  managePermitLinkedPermitAccountInfo: (accountId: string, permitId: string) =>
    `${BASE}/manage-permit/accounts/${accountId}/permits/${permitId}/account-info`,
  managePermitLinkedPermitVehicles: (accountId: string, permitId: string) =>
    `${BASE}/manage-permit/accounts/${accountId}/permits/${permitId}/vehicles`,
  managePermitLinkedPermitAddReplacement: (accountId: string, permitId: string) =>
    `${BASE}/manage-permit/accounts/${accountId}/permits/${permitId}/vehicles/add-replacement`,
  managePermitLinkedPermitAddVehicle: (accountId: string, permitId: string) =>
    `${BASE}/manage-permit/accounts/${accountId}/permits/${permitId}/vehicles/add-vehicle`,
  managePermitLinkedPermitEditVehicle: (accountId: string, permitId: string, vehicleId: string) =>
    `${BASE}/manage-permit/accounts/${accountId}/permits/${permitId}/vehicles/edit-vehicle/${vehicleId}`,
  managePermitLinkedPermitDrivers: (accountId: string, permitId: string) =>
    `${BASE}/manage-permit/accounts/${accountId}/permits/${permitId}/drivers`,
  managePermitLinkedPermitAddDriver: (accountId: string, permitId: string) =>
    `${BASE}/manage-permit/accounts/${accountId}/permits/${permitId}/drivers/add-driver`,
  managePermitLinkedPermitEditDriver: (accountId: string, permitId: string, driverId: string) =>
    `${BASE}/manage-permit/accounts/${accountId}/permits/${permitId}/drivers/edit-driver/${driverId}`,
  managePermitLinkedPermitAccessCards: (accountId: string, permitId: string) =>
    `${BASE}/manage-permit/accounts/${accountId}/permits/${permitId}/access-cards`,
  managePermitLinkedPermitDocuments: (accountId: string, permitId: string) =>
    `${BASE}/manage-permit/accounts/${accountId}/permits/${permitId}/documents`,
  managePermitLinkedPermitAddTaxCert: (accountId: string, permitId: string) =>
    `${BASE}/manage-permit/accounts/${accountId}/permits/${permitId}/documents/add-tax-certificate`,
  managePermitLinkedPermitEditContact: (accountId: string, permitId: string) =>
    `${BASE}/manage-permit/accounts/${accountId}/permits/${permitId}/account-info/edit-contact`,
  managePermitAccountAddPermit: (accountId: string) =>
    `${BASE}/manage-permit/accounts/${accountId}/add-permit`,
  managePermitLinkGarage: `${BASE}/manage-permit/accounts/link-garage`,
  managePermitPickupSchedule: `${BASE}/manage-permit/pickup-schedule`,
  managePermitTasksSignContract: (taskId: string) =>
    `${BASE}/manage-permit/tasks/sign-contract/${taskId}`,
  reserve: '/reserve',
  reservePayment: '/reserve/payment',
  reserveMonthlySignup: '/reserve/monthly-signup',
  reserveConfirmation: '/reserve/confirmation',
} as const;

/** Relative segments for React Router <Route path="..."> definitions */
export const MY_ACCOUNT_ROUTE_SEGMENTS = {
  profile: 'profile',
  editProfile: 'profile/edit',
  addAddress: 'profile/addresses/add',
} as const;

export const AUTH_ROUTES = {
  login: '/login',
  sso: '/sso',
} as const;

/** Quote entry route (public — user may need to log in) */
export const QUOTE_ROUTES = {
  quote: (id: string) => `/quote/${id}`,
} as const;

/** QR scan entry routes (public) */
export const QR_ROUTES = {
  /** /qr/:deviceId */
  device: (deviceId: string) => `/qr/${deviceId}`,
  /** /qr/:garageId/:deviceId */
  garageDevice: (garageId: string, deviceId: string) => `/qr/${garageId}/${deviceId}`,
  /** /z/:laneNumber */
  lane: (laneNumber: string) => `/z/${laneNumber}`,
} as const;

/**
 * Whether a pathname is one of the QR scan-to-pay booking surfaces above.
 *
 * ## Invariants:
 * - Matches on the route PREFIX, not an exact path: both entries are dynamic
 *   (`/qr/[...slug]`, `/z/[laneNumber]`), so there is no literal to compare against.
 * - Expects the company-stripped path that `@/lib/app-router`'s `usePathname` returns. A raw
 *   `/c/{companyId}/z/412` does not match, which is the correct answer for a caller holding the
 *   browser path — it should strip first rather than have this widen to a substring search.
 *
 * @param pathname company-stripped pathname, e.g. `/z/412`
 */
export function isQrScanPath(pathname: string): boolean {
  return pathname.startsWith('/qr/') || pathname.startsWith('/z/');
}

/** Touchless payment routes (public) */
export const TOUCHLESS_ROUTES = {
  /** /touchless/:id */
  ticket: (id: string) => `/touchless/${id}`,
  /** /t/:id (short alias) */
  ticketShort: (id: string) => `/t/${id}`,
} as const;

/** Reservation extend route (public, guest-accessible via hash token) */
export const EXTEND_ROUTES = {
  /** /extend/:id#token=Y */
  extend: (id: string) => `/extend/${id}`,
} as const;

/** Reservation cancel route (public, guest-accessible via hash token) */
export const CANCEL_ROUTES = {
  /** /cancel/:id#token=Y */
  cancel: (id: string) => `/cancel/${id}`,
} as const;

/** Booking redirect routes (public, redirect to /reserve with garageId) */
export const BOOKING_ROUTES = {
  /** /h/:id — hourly booking */
  hourly: (id: string) => `/h/${id}`,
  /** /d/:id — daily/hourly booking */
  daily: (id: string) => `/d/${id}`,
  /** /m/:id — monthly booking */
  monthly: (id: string) => `/m/${id}`,
  /** /p/:id — permit/monthly booking */
  permit: (id: string) => `/p/${id}`,
} as const;

/**
 * Public citation pay routes (public, guest-accessible).
 *
 * Both live under `/ct`, never `/c`. `/c/{segment}` is a reserved shape: on a company-routing
 * host the api's `DomainHelper.getDomainName` reads the segment after `/c/` as the COMPANY
 * identifier and folds it into the resolved domain, so a citation number there resolves as a
 * company and the lookup is refused. Keeping the whole citation namespace off `/c` means no
 * route here depends on that parser detail.
 */
export const CITATION_ROUTES = {
  /** /ct — manual lookup by citation number + lot code + plate */
  lookup: '/ct',
  /** /ct/:ticketNumber#g=<guid> — QR arrival; the GUID stays in the fragment */
  qr: (ticketNumber: string) => `/ct/${ticketNumber}`,
} as const;

/** Damage claim route (public, triggered from POS/QR codes) */
export const DAMAGE_CLAIM_ROUTES = {
  damageClaim: '/damageclaim',
} as const;

/** Public static/informational pages */
export const PUBLIC_ROUTES = {
  terms: '/terms',
  privacy: '/privacy',
  about: '/about',
  accessibility: '/accessibility',
  contact: '/contact',
  help: '/help',
  /** Operator-created white-label tab pages: `/content/[slug]`. */
  content: '/content',
} as const;
